import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { authApi } from "../api/auth";
import { useAuth } from "../auth/AuthContext";

/**
 * Three-step avatar upload widget for the signed-in user:
 * 1) ask BE for a presigned PUT URL
 * 2) PUT the file directly to MinIO (bytes never touch the backend)
 * 3) confirm with BE so the DB row + cached avatarUrl update
 * Refreshes the auth context on success so every <Avatar> on the page picks
 * up the new URL immediately without a hard reload.
 */
export function AvatarUpload({ size = 96 }: { size?: number }) {
  const { t } = useTranslation();
  const { state, refresh } = useAuth();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (state.status !== "authenticated") return null;
  const user = state.user;
  const initials =
    `${user.firstName?.[0] ?? ""}${user.lastName?.[0] ?? ""}`.toUpperCase() || "—";

  async function onPick(file: File) {
    setErr(null);
    setBusy(true);
    try {
      const presigned = await authApi.presignAvatar(file.type, file.size);
      const putRes = await fetch(presigned.url, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });
      if (!putRes.ok) throw new Error(`Upload failed (${putRes.status})`);
      await authApi.confirmAvatar(presigned.objectKey);
      await refresh();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  async function onRemove() {
    setErr(null);
    setBusy(true);
    try {
      await authApi.deleteAvatar();
      await refresh();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
      {user.avatarUrl ? (
        <img
          src={user.avatarUrl}
          alt=""
          style={{
            width: size,
            height: size,
            borderRadius: "50%",
            objectFit: "cover",
            border: "1px solid var(--border)",
          }}
        />
      ) : (
        <div
          aria-hidden
          style={{
            width: size,
            height: size,
            borderRadius: "50%",
            background: "var(--brand-primary)",
            color: "var(--on-primary)",
            display: "grid",
            placeItems: "center",
            fontWeight: 600,
            fontSize: Math.round(size * 0.32),
          }}
        >
          {initials}
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            type="button"
            className="btn"
            disabled={busy}
            onClick={() => inputRef.current?.click()}
          >
            {user.avatarUrl
              ? t("profile.avatar.replace", "Replace photo")
              : t("profile.avatar.upload", "Upload photo")}
          </button>
          {user.avatarUrl && (
            <button type="button" className="btn" disabled={busy} onClick={onRemove}>
              {t("profile.avatar.remove", "Remove")}
            </button>
          )}
          <input
            ref={inputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void onPick(f);
            }}
          />
        </div>
        <span className="muted" style={{ fontSize: 12 }}>
          {t("profile.avatar.hint", "PNG, JPEG or WEBP. Max 2 MB.")}
        </span>
        {err && <span style={{ color: "var(--err)", fontSize: 12 }}>{err}</span>}
      </div>
    </div>
  );
}
